import React, { useState, useEffect } from 'react';
import { Typography, Container, Box, Card, CardContent, Rating, Divider, CircularProgress } from '@mui/material';

function FeedbackPage() {
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [ratingCount, setRatingCount] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setLoading(true);
        // Ensure we're passing the action parameter
        const feedbackEndpoint = `${process.env.REACT_APP_FEEDBACK_SHEET_URL}?action=getFeedback`;
        
        const response = await fetch(feedbackEndpoint);
        const text = await response.text(); // First get as text to debug
        
        try {
          const data = JSON.parse(text);
          if (data.result === 'success') {
            setFeedback(data.feedbackData || []);
            setAverageRating(parseFloat(data.averageRating) || 0);
            setRatingCount(data.ratingCount || 0);
          } else {
            console.error('Error from API:', data.error || 'Unknown error');
            setError('Failed to load feedback data');
          }
        } catch (parseError) {
          console.error('Failed to parse response:', text);
          setError('Invalid response from server');
        }
      } catch (err) {
        setError('An error occurred while fetching feedback');
        console.error('Feedback fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchFeedback();
  }, []);

  // Format the timestamp for display
  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
    <div className="account-page">
      <div className="account-container">

      <Container sx={{ my: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading feedback...
        </Typography>
      </Container>
      </div>
      </div>
    );
  }

  if (error) {
    return (
    <div className="account-page">
      <div className="account-container">

      <Container sx={{ my: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Container>
      </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="account-container">

    <Container maxWidth="md" sx={{ my: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          User Feedback
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          Here's what our users are saying about InstantFlows
        </Typography>
        
        <Box sx={{ my: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Typography variant="h4" component="span">
            {averageRating}
          </Typography>
          <Rating 
            value={averageRating} 
            precision={0.1} 
            readOnly 
            size="large"
          />
          <Typography variant="subtitle1" component="span" sx={{ opacity: 0.7 }}>
            ({ratingCount} {ratingCount === 1 ? 'rating' : 'ratings'})
          </Typography>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 4 }} />
      
      {feedback.length === 0 ? (
        <Typography variant="body1" textAlign="center">
          No approved feedback available yet.
        </Typography>
      ) : (
        feedback.map((item, index) => (
          <Card key={index} sx={{ mb: 3, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Rating value={Number(item.rating)} readOnly />
                <Typography variant="caption" color="text.secondary">
                  {formatDate(item.timestamp)}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: item.response ? 2 : 0 }}>
                "{item.feedback}"
              </Typography>
              
              {/* Display admin response if available */}
              {item.response && (
                <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid var(--tertiary-color)' }}>
                  <Typography variant="subtitle2" color="var(--tertiary-color)">
                    Response from InstantFlows team:
                  </Typography>
                  <Typography variant="body2">
                    {item.response}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </Container>
    </div>
    </div>
  );
}

export default FeedbackPage;